import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  RefreshCcw,
  Users,
  UserCheck,
  UserX,
  Building2,
  Mail,
  Phone,
  Shield,
  CalendarDays,
  BriefcaseBusiness,
} from "lucide-react";
import { EmployeesService } from "../../../services/employees.service";
import { warehouseService } from "../../../services/warehouse.service";
import { getAuthSession } from "../../AdminAuth/components/authStore";

const BORDER = "#f2c6cc";

function getInitials(name = "Employee") {
  const parts = String(name || "Employee").trim().split(/\s+/);
  return `${parts?.[0]?.[0] || "E"}${parts?.[1]?.[0] || ""}`.toUpperCase();
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div
      className="rounded-[22px] border bg-white p-5 shadow-[0_14px_34px_rgba(216,106,120,0.08)] transition hover:-translate-y-[1px] hover:shadow-[0_18px_42px_rgba(216,106,120,0.12)]"
      style={{ borderColor: BORDER }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </div>
          <div className="mt-2 text-[30px] font-bold text-slate-900">
            {value}
          </div>
          <div className="mt-1 text-[12px] text-slate-500">{sub}</div>
        </div>

        <div className="grid h-12 w-12 place-items-center rounded-[16px] bg-[#fff2f4] text-[#d86a78]">
          <Icon size={21} />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ value }) {
  const active = String(value || "").toLowerCase() === "active";

  return (
    <span
      className={[
        "inline-flex rounded-full border px-3 py-1 text-[12px] font-semibold",
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-rose-200 bg-rose-50 text-rose-700",
      ].join(" ")}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function MiniInfo({ icon: Icon, label, value }) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-[12px] bg-[#fffafb] px-3 py-2">
      <Icon size={14} className="shrink-0 text-[#d86a78]" />
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
          {label}
        </div>
        <div className="truncate text-[12px] font-medium text-slate-600">
          {value || "-"}
        </div>
      </div>
    </div>
  );
}

function FilterChip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "h-[36px] rounded-full border px-4 text-[13px] font-semibold transition",
        active
          ? "border-[#d86a78] bg-[#d86a78] text-white shadow-[0_10px_24px_rgba(216,106,120,0.22)]"
          : "border-[#f2c6cc] bg-white text-slate-500 hover:bg-[#fff6f7] hover:text-[#d86a78]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function WarehouseEmployees() {
  const navigate = useNavigate();

  const session = getAuthSession() || {};
  const warehouseAccess = session?.warehouseAccess || {};
  const selectedWarehouseId =
    session?.selectedWarehouseId || warehouseAccess?.assignedWarehouseId || "";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [warehouseMap, setWarehouseMap] = useState({});
  const [error, setError] = useState("");

  const loadData = useCallback(
    async (showRefresh = false) => {
      try {
        setError("");
        if (showRefresh) setRefreshing(true);
        else setLoading(true);

        const [employeesRes, warehouseRes] = await Promise.all([
          EmployeesService.listAllForDashboard({
            page: 1,
            limit: 300,
            userType: "warehouse_employee",
            warehouseId: selectedWarehouseId || undefined,
          }),
          warehouseService
            .listWarehouses({ page: 1, limit: 300 })
            .catch(() => ({})),
        ]);

        const warehouseRows =
          warehouseRes?.items ||
          warehouseRes?.data?.items ||
          warehouseRes?.rows ||
          warehouseRes?.data?.rows ||
          warehouseRes?.warehouses ||
          [];

        const warehouseNameMap = Object.fromEntries(
          warehouseRows
            .filter(Boolean)
            .map((w) => [
              w.id,
              w.name ||
                w.warehouseName ||
                w.code ||
                w.locationName ||
                w.slug ||
                w.id,
            ])
        );

        const normalized = Array.isArray(employeesRes) ? employeesRes : [];

        const warehouseOnly = normalized.filter((emp) => {
          const type = String(emp?.userType || emp?.role || "").toLowerCase();
          return type === "warehouse_employee";
        });

        const filteredBySelectedWarehouse = selectedWarehouseId
          ? warehouseOnly.filter((emp) => {
              const assigned = emp?.assignedWarehouseId;
              const allowed = Array.isArray(emp?.allowedWarehouseIds)
                ? emp.allowedWarehouseIds
                : [];

              return (
                assigned === selectedWarehouseId ||
                allowed.includes(selectedWarehouseId)
              );
            })
          : warehouseOnly;

        setWarehouseMap(warehouseNameMap);
        setEmployees(filteredBySelectedWarehouse);
      } catch (err) {
        console.error("Failed to load warehouse employees", err);
        setEmployees([]);
        setError(err?.message || "Failed to load warehouse employees");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedWarehouseId]
  );

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  const stats = useMemo(() => {
    const total = employees.length;
    const active = employees.filter(
      (item) => String(item?.status).toLowerCase() === "active"
    ).length;
    const inactive = Math.max(0, total - active);
    const assigned = employees.filter((item) => item?.assignedWarehouseId).length;

    return { total, active, inactive, assigned };
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();

    return employees.filter((emp) => {
      const empStatus = String(emp?.status || "").toLowerCase();

      if (statusFilter === "active" && empStatus !== "active") return false;
      if (statusFilter === "inactive" && empStatus === "active") return false;

      if (!q) return true;

      const warehouseText =
        warehouseMap[emp?.assignedWarehouseId] || emp?.assignedWarehouseId || "";

      return [
        emp?.name,
        emp?.email,
        emp?.phone,
        emp?.employeeId,
        emp?.role,
        emp?.department,
        warehouseText,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [employees, search, warehouseMap, statusFilter]);

  const openEmployeeDetails = (emp) => {
    if (!emp?.id) return;
    navigate(`/admin/dashboard/employees/${emp.id}`);
  };

  return (
    <div className="space-y-6">
      <div
        className="overflow-hidden rounded-[26px] border bg-white shadow-[0_18px_45px_rgba(216,106,120,0.09)]"
        style={{ borderColor: BORDER }}
      >
        <div className="relative p-5 sm:p-6">
          <div className="pointer-events-none absolute right-0 top-0 h-32 w-32 rounded-bl-full bg-[#fff1f3]" />

          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#f2c6cc] bg-[#fff8f9] px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
                <Users size={14} />
                Warehouse Team
              </div>

              <h1 className="warehouse-page-title mt-3">
                Employees
              </h1>

              <p className="mt-2 max-w-2xl text-[14px] leading-6 text-slate-500">
                View warehouse employees, check status, contact details and open
                the complete employee profile.
              </p>

              {selectedWarehouseId ? (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#f2c6cc] bg-white px-3 py-1 text-[12px] text-slate-600">
                  <Building2 size={14} />
                  <span>
                    Current warehouse: {" "}
                    <span className="font-semibold text-slate-700">
                      {warehouseMap[selectedWarehouseId] || selectedWarehouseId}
                    </span>
                  </span>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative min-w-[280px]">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, phone..."
                  className="h-[46px] w-full rounded-[16px] border border-[#f2c6cc] bg-white pl-10 pr-4 text-[14px] text-slate-700 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                />
              </div>

              <button
                type="button"
                onClick={() => loadData(true)}
                disabled={refreshing}
                className="inline-flex h-[46px] items-center justify-center gap-2 rounded-[16px] bg-[#d86a78] px-5 text-[14px] font-semibold text-white transition hover:bg-[#c95b69] disabled:opacity-70"
              >
                <RefreshCcw
                  size={16}
                  className={refreshing ? "animate-spin" : ""}
                />
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Users}
          label="Total"
          value={stats.total}
          sub="Warehouse employees"
        />
        <StatCard
          icon={UserCheck}
          label="Active"
          value={stats.active}
          sub="Currently active"
        />
        <StatCard
          icon={UserX}
          label="Inactive"
          value={stats.inactive}
          sub="Need attention"
        />
        <StatCard
          icon={Building2}
          label="Assigned"
          value={stats.assigned}
          sub="With warehouse mapped"
        />
      </div>

      <div
        className="rounded-[26px] border bg-white shadow-[0_14px_36px_rgba(216,106,120,0.07)]"
        style={{ borderColor: BORDER }}
      >
        <div className="flex flex-col gap-4 border-b border-[#f7d9de] px-5 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-[20px] font-bold text-slate-900">
              Employee Directory
            </h2>
            <p className="mt-1 text-[13px] text-slate-500">
              {filteredEmployees.length} result
              {filteredEmployees.length === 1 ? "" : "s"} shown
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <FilterChip
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
            >
              All
            </FilterChip>
            <FilterChip
              active={statusFilter === "active"}
              onClick={() => setStatusFilter("active")}
            >
              Active
            </FilterChip>
            <FilterChip
              active={statusFilter === "inactive"}
              onClick={() => setStatusFilter("inactive")}
            >
              Inactive
            </FilterChip>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4 p-5 sm:p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[128px] animate-pulse rounded-[22px] bg-[#fff6f7]"
              />
            ))}
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-3 text-[14px] text-rose-700">
              {error}
            </div>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#fff6f7] text-[#d86a78]">
              <Users size={26} />
            </div>
            <h3 className="mt-4 text-[18px] font-bold text-slate-900">
              No warehouse employees found
            </h3>
            <p className="mx-auto mt-2 max-w-xl text-[14px] leading-6 text-slate-500">
              No employee matches your current search or selected filter.
            </p>
          </div>
        ) : (
          <div className="space-y-4 p-5 sm:p-6">
            {filteredEmployees.map((emp) => {
              const warehouseName =
                warehouseMap[emp?.assignedWarehouseId] ||
                emp?.assignedWarehouseId ||
                "-";

              return (
                <div
                  key={emp.id}
                  className="w-full rounded-[22px] border border-[#f2c6cc] bg-white p-4 text-left shadow-[0_10px_26px_rgba(216,106,120,0.06)] transition hover:-translate-y-[1px] hover:border-[#d86a78] hover:shadow-[0_18px_42px_rgba(216,106,120,0.12)]"
                >
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex min-w-0 flex-1 gap-4">
                      <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#fff1f3] text-[17px] font-extrabold text-[#d86a78] ring-1 ring-[#f2c6cc]">
                        {getInitials(emp?.name)}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => openEmployeeDetails(emp)}
                            className="truncate text-left text-[16px] font-semibold text-slate-800 transition hover:text-[#d86a78] hover:underline"
                            title="Open employee details"
                          >
                            {emp?.name || "-"}
                          </button>

                          <StatusBadge value={emp?.status} />

                          <span className="inline-flex rounded-full border border-[#f2c6cc] bg-[#fff8f9] px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
                            {emp?.role || "warehouse_employee"}
                          </span>
                        </div>

                        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                          <MiniInfo icon={Mail} label="Email" value={emp?.email} />
                          <MiniInfo icon={Phone} label="Phone" value={emp?.phone} />
                          <MiniInfo
                            icon={Building2}
                            label="Warehouse"
                            value={warehouseName}
                          />
                          <MiniInfo
                            icon={Shield}
                            label="Department"
                            value={emp?.department || "No department"}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[300px]">
                      <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fffafb] px-4 py-3">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                          <BriefcaseBusiness size={13} />
                          Employee ID
                        </div>
                        <div className="mt-1 text-[14px] font-bold text-slate-800">
                          {emp?.employeeId || "-"}
                        </div>
                      </div>

                      <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fffafb] px-4 py-3">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                          <CalendarDays size={13} />
                          Joining
                        </div>
                        <div className="mt-1 text-[14px] font-bold text-slate-800">
                          {emp?.joiningDate || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
